import {
  BillingInfo,
  ContractInfo,
  CustomPlanFilter,
  CustomPlanInfo,
  UserLanguageEnum,
  SubscriptionAccountTypeEnum,
  SubscriptionInfo,
  UserForm,
  UserInfo,
} from "@/entities/account/account.types"
import { CUSTOM_PLAN_PAGE_SIZE } from "@/entities/account/account.constants"
import { api } from "@/shared/lib/axios/axios.api"
import {
  ADMIN_API_PREFIX,
  API_PREFIX,
  AUTH_API_PREFIX,
  MP_AUTH_API_PREFIX,
  MT_API_PREFIX,
} from "@/shared/lib/axios/axios.constants"
import { handleAxiosError } from "@/shared/lib/axios/axios.lib"
import axios, { HttpStatusCode } from "axios"

export const getAccountInfo = async ({
  accountId,
}: {
  accountId: string
}): Promise<UserInfo> => {
  try {
    const response = await api.get(`${AUTH_API_PREFIX}/accounts/${accountId}`)
    return response.data
  } catch (error) {
    return handleAxiosError(error)
  }
}

export const getBillingInfo = async (): Promise<BillingInfo> => {
  try {
    const response = await api.get<BillingInfo>(`${MT_API_PREFIX}/account`, {
      params: { queryType: SubscriptionAccountTypeEnum.BillingInfo },
    })
    return response.data
  } catch (error) {
    return handleAxiosError(error)
  }
}

export const getUserInfo = async (): Promise<UserInfo> => {
  try {
    const response = await api.get(`${MP_AUTH_API_PREFIX}/accounts`)
    return response.data
  } catch (error) {
    return handleAxiosError(error)
  }
}

export const getCurrentSubscriptionContractInfo =
  async (): Promise<ContractInfo | null> => {
    try {
      const response = await api.get<ContractInfo>(`${MT_API_PREFIX}/account`, {
        params: {
          queryType: SubscriptionAccountTypeEnum.Contract,
        },
      })
      return response.data
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === HttpStatusCode.NotFound
      ) {
        return null
      }
      return handleAxiosError(error)
    }
  }

export const getCountryCode = async ({
  ip,
}: {
  ip: string
}): Promise<string> => {
  try {
    const response = await api.get<string>(`${API_PREFIX}/ip-conversion`, {
      params: { ip },
    })
    return response.data
  } catch (error) {
    return handleAxiosError(error)
  }
}

export const getCustomPlanInfo = async ({
  pageParam,
  filter,
}: {
  pageParam: unknown
  filter: CustomPlanFilter
}): Promise<CustomPlanInfo[]> => {
  const searchValue = filter.searchValue.trim()
  try {
    const response = await api.get(`${ADMIN_API_PREFIX}/mt/contracts`, {
      // The params serializer sends `undefined` as a string, so omit unset filters.
      params: {
        page: pageParam,
        items_per_page: CUSTOM_PLAN_PAGE_SIZE,
        ...(searchValue && { [filter.searchType]: searchValue }),
        ...(filter.state && { state: filter.state }),
      },
    })
    return response.data
  } catch (error) {
    return handleAxiosError(error)
  }
}

export const editProfile = async ({
  userForm,
}: {
  userForm: Pick<UserForm, "email" | "userName" | "phoneNumber" | "companyName">
}) => {
  const response = await api.put(`${MP_AUTH_API_PREFIX}/accounts`, {
    email: userForm.email,
    userName: userForm.userName,
    phoneNumber: userForm.phoneNumber,
    companyName: userForm.companyName,
  })

  return response.data
}

export const deleteAccount = async () => {
  try {
    const response = await api.delete<never>(`${MP_AUTH_API_PREFIX}/accounts`)
    return response.data
  } catch (error) {
    return handleAxiosError(error)
  }
}

export const getSubscriptionInfo = async ({
  subscriptionId,
}: {
  subscriptionId: string
}): Promise<SubscriptionInfo> => {
  try {
    const response = await api.get<SubscriptionInfo>(
      `${MT_API_PREFIX}/account`,
      {
        params: {
          queryType: SubscriptionAccountTypeEnum.Subscription,
          subscriptionId,
        },
      }
    )
    return response.data
  } catch (error) {
    return handleAxiosError(error)
  }
}

export const changeLanguage = async ({
  language,
}: {
  language: UserLanguageEnum
}) => {
  try {
    const response = await api.patch<string>(
      `${MP_AUTH_API_PREFIX}/accounts/language`,
      {
        language,
      }
    )

    return response.data
  } catch (error) {
    return handleAxiosError(error)
  }
}
